import type { SponsorCampaign } from "../campaignTypes";

/**
 * Stadium Tour ABM portal config (anonymous partner presentation).
 * Swap brand/image/markets here without touching layout components.
 */

export const PARTNER_LOGO_URL = "";
export const HERO_STADIUM_IMAGE_URL = "/images/partners/amex-esb/stadiums/wrigley.jpg";
export const LOCKER_ROOM_IMAGE_URL = "/images/partners/amex-esb/stadiums/arrowhead.jpg";
export const FIELD_IMAGE_URL = "/images/partners/amex-esb/stadiums/petco.jpg";
export const TAPINGS_HERO_IMAGE_URL = "https://storage.googleapis.com/cuo-website/Head%20Image.jpg";

export const STADIUM_GALLERY_IMAGE_URLS = {
  wrigley: "/images/partners/amex-esb/stadiums/wrigley.jpg",
  arrowhead: "/images/partners/amex-esb/stadiums/arrowhead.jpg",
  petco: "/images/partners/amex-esb/stadiums/petco.jpg",
  soldier: "/images/partners/amex-esb/stadiums/soldier.jpg",
  citizens: "/images/partners/amex-esb/stadiums/citizens.jpg",
  citi: "/images/partners/amex-esb/stadiums/citi.jpg",
} as const;

export const TALENT_IMAGE_URLS = {
  marcello: "https://storage.googleapis.com/cuo-website/Marcello+Hernandez+at+Paragon.jpg",
  chris: "https://storage.googleapis.com/cuo-website/Chris+Distefano+at+Wilson.jpg",
  leslie: "https://storage.googleapis.com/cuo-website/Leslie+Jones+KidSuper.jpg",
  desert: "https://storage.googleapis.com/cuo-website/Desert+5.jpg",
  crowd: "https://storage.googleapis.com/cuo-website/UO_112923-024.jpg",
  youngCrowd: "https://storage.googleapis.com/cuo-website/UO_071324-080.jpg",
  bestShows: "https://storage.googleapis.com/cuo-website/Best+Comedy+Shows+.jpg",
} as const;

export type DkStadiumMarket = {
  id: string;
  city: string;
  team: string;
  stadium: string;
  showsLabel: string;
  imageUrl: string;
  statusNote: string;
};

export type DkStadiumSpace = {
  id: string;
  name: string;
  capacityLabel: string;
  detail: string;
};

export const dkStadiumConfig = {
  brandName: "Partner",
  brandLogoUrl: PARTNER_LOGO_URL,
  accentColor: "#72BB3B",
  tourTitle: "ComedyUO Stadium Tour",
  timingLabel: "April to June 2027",
  positioning:
    "Intimate live comedy in surprising spaces within America's most iconic stadiums, paired with premium hospitality.",
  partnershipThesis:
    "A partner that lives at the intersection of sports, entertainment and fandom. This tour gives the brand a new way to bring fans inside iconic stadiums.",
  whyBrandBody: [
    "The right partner turns sports fandom into participation. ComedyUO brings that same energy inside major-league stadiums, giving fans an entirely new way to experience the places and teams they love.",
    "Each stop transforms an iconic stadium space into an intimate comedy venue. A national partner can connect the full tour while creating market-specific moments around each city, stadium and fan base.",
    "The audience span is sports fans, comedy fans and young professionals who spend on live entertainment and premium experiences, with the brand positioned at the center of the night.",
  ],
  activations: {
    naming: [
      "Clear presented-by rights in tour verbiage (subject to team approval)",
      "Brand presence woven into the night, not stuck on as an afterthought",
    ],
    fanAccess: [
      "Full ticket allocations and reserved access for partner members",
      "Option to distribute tickets through the partner's owned channels",
    ],
    onSite: [
      "On-site signage by or behind the stage, on walls, or even on seats",
      "Hospitality moments as guests arrive, connect and stay after the set",
    ],
    talentHospitality: [
      "Talent rotating room to room across the night",
      "Premium back-of-house and green-room moments for guests and partners",
    ],
    content: [
      "Tagged in every post, custom promo video content and dedicated grid posts",
      "Collaborations with stadium accounts around each market",
    ],
    social: [
      "Owned media distribution across ComedyUO and partner channels",
      "A behind-the-scenes series following comedians through locker rooms, tunnels and fields",
    ],
    market: [
      "Market-by-market activations shaped around each team and city",
      "Room to build a repeatable national entertainment property together",
    ],
  },
  formatSteps: [
    {
      id: "arrive",
      title: "Arrive",
      body: "Guests enter the stadium and experience spaces most fans never get to occupy.",
    },
    {
      id: "connect",
      title: "Connect",
      body: "Food, drinks and the room itself become part of the night before the first joke lands.",
    },
    {
      id: "laugh",
      title: "Laugh",
      body: "A close-up live set begins. Meanwhile, comedians rotate from one room to the next to hit their spots as the night progresses.",
    },
    {
      id: "stay",
      title: "Stay",
      body: "The bar stays open after the show so the stadium keeps feeling like the place to be.",
    },
  ],
  spaces: [
    {
      id: "locker",
      name: "Locker rooms",
      capacityLabel: "~150 tickets",
      detail: "Intimate, high-status rooms fans rarely enter.",
    },
    {
      id: "cage",
      name: "Batting cage",
      capacityLabel: "~200 tickets",
      detail: "Surprising scale with a true on-field energy.",
    },
    {
      id: "bullpen",
      name: "Bullpen",
      capacityLabel: "~75 tickets",
      detail: "A tighter room for a more private comedy night.",
    },
    {
      id: "media",
      name: "Media room",
      capacityLabel: "~100 tickets",
      detail: "The space where all your favorite players greet you through the TV.",
    },
    {
      id: "warning",
      name: "Warning track",
      capacityLabel: "~100 tickets",
      detail: "Close enough to the action but with an intimate touch.",
    },
  ] as DkStadiumSpace[],
  markets: [
    {
      id: "cubs",
      city: "Chicago",
      team: "Cubs",
      stadium: "Wrigley Field",
      showsLabel: "6 shows",
      imageUrl: STADIUM_GALLERY_IMAGE_URLS.wrigley,
      statusNote: "Team conversations underway. LOI process in progress.",
    },
    {
      id: "chiefs",
      city: "Kansas City",
      team: "Chiefs",
      stadium: "Arrowhead Stadium",
      showsLabel: "3 shows",
      imageUrl: STADIUM_GALLERY_IMAGE_URLS.arrowhead,
      statusNote: "Team conversations underway. LOI process in progress.",
    },
    {
      id: "padres",
      city: "San Diego",
      team: "Padres",
      stadium: "Petco Park",
      showsLabel: "2 shows",
      imageUrl: STADIUM_GALLERY_IMAGE_URLS.petco,
      statusNote: "Team conversations underway. LOI process in progress.",
    },
    {
      id: "bears",
      city: "Chicago",
      team: "Bears",
      stadium: "Soldier Field",
      showsLabel: "3 shows",
      imageUrl: STADIUM_GALLERY_IMAGE_URLS.soldier,
      statusNote: "Team conversations underway. LOI process in progress.",
    },
    {
      id: "phillies",
      city: "Philadelphia",
      team: "Phillies",
      stadium: "Citizens Bank Park",
      showsLabel: "3 shows",
      imageUrl: STADIUM_GALLERY_IMAGE_URLS.citizens,
      statusNote: "Team conversations underway. LOI process in progress.",
    },
    {
      id: "mets",
      city: "New York",
      team: "Mets",
      stadium: "Citi Field",
      showsLabel: "3 shows",
      imageUrl: STADIUM_GALLERY_IMAGE_URLS.citi,
      statusNote: "Team conversations underway. LOI process in progress.",
    },
  ] as DkStadiumMarket[],
  images: {
    logo: PARTNER_LOGO_URL,
    hero: HERO_STADIUM_IMAGE_URL,
    locker: LOCKER_ROOM_IMAGE_URL,
    field: FIELD_IMAGE_URL,
    tapingsHero: TAPINGS_HERO_IMAGE_URL,
    gallery: STADIUM_GALLERY_IMAGE_URLS,
    talent: TALENT_IMAGE_URLS,
  },
  cta: {
    headline: "Let's work together.",
    body: "If this tour is the right next chapter, let's map it together.",
    email: "mike@comedyuo.com",
    name: "Mike Zbyszewski",
    role: "Sales & Partnerships, ComedyUO",
  },
} as const;

/** Helmet / gate fields for the partner route. */
export const draftKingsStadiumCampaign: SponsorCampaign = {
  campaignSlug: "draft-kings-stadium",
  sponsorName: dkStadiumConfig.brandName,
  sponsorLogo: "https://storage.googleapis.com/cuo-website/comedyuo-logo-white.png",
  sponsorWordmark: "ComedyUO",
  comedyuoLogo: "https://storage.googleapis.com/cuo-website/comedyuo-logo-white.png",
  accentColor: dkStadiumConfig.accentColor,
  eyebrow: "ComedyUO · Overview",
  campaignName: "ComedyUO Overview",
  shareTitle: "ComedyUO Overview",
  shareImage: "/images/og-default-katz-deli.jpg",
  headline: dkStadiumConfig.tourTitle,
  venue: "Iconic stadiums across America",
  dateRange: dkStadiumConfig.timingLabel,
  eventDate: "April to June 2027",
  reportingPeriod: "Partnership concept",
  campaignSummary: dkStadiumConfig.positioning,
  heroImage: dkStadiumConfig.images.hero,
  heroImageAlt: "Stadium setting for the ComedyUO Stadium Tour concept",
  heroCtaLabel: "Enter the tour",
  heroCtaHref: "?tab=stadium",
  featuredMetric: {
    label: "Tour window",
    value: "Apr–Jun 2027",
    detail: "Proposed national stadium comedy tour",
  },
  glanceFields: [],
  activations: [],
  activationsSection: { eyebrow: "", title: "", description: "" },
  platformPerformance: {
    eyebrow: "",
    title: "",
    description: "",
    rows: [],
    totalImpressionsLabel: "",
    totalImpressionsValue: "",
  },
  kpiSection: { eyebrow: "", title: "", description: "", groups: [] },
  gallery: { eyebrow: "", title: "", images: [] },
  assets: { eyebrow: "", title: "", description: "", reportPdf: false, items: [] },
  closing: {
    thankYouHeadline: dkStadiumConfig.cta.headline,
    summary: dkStadiumConfig.cta.body,
    highlightCallout: dkStadiumConfig.partnershipThesis,
    contact: {
      name: dkStadiumConfig.cta.name,
      role: dkStadiumConfig.cta.role,
      email: dkStadiumConfig.cta.email,
    },
  },
  allowedEmailDomains: ["comedyuo.com"],
  nav: [
    { id: "about", label: "About Us", href: "?tab=about" },
    { id: "stadium", label: "Stadium Tour", href: "?tab=stadium" },
    { id: "tapings", label: "Comedy Tapings", href: "?tab=tapings" },
  ],
  isSamplePlaceholder: false,
};
